// Governance Markdown rendering/editing primitives. Editability is passed from the server projection; this
// component never decides which document keys are writable. Raw HTML is not enabled in the Markdown renderer.

import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Btn, Panel, StatusPill } from '../primitives.tsx';
import type { GovernanceComponentView, GovernanceOwnership } from '../governance-projections.ts';

const OWNERSHIP_LABEL: Readonly<Record<GovernanceOwnership, string>> = {
  central: 'Central',
  combined: 'Combined',
  company: 'Company',
  generated: 'Generated',
};

export function OwnershipLabel({ ownership }: { readonly ownership: GovernanceOwnership }) {
  const tone = ownership === 'company' ? 'attention' : ownership === 'generated' ? 'go' : 'neutral';
  return <StatusPill tone={tone}>{OWNERSHIP_LABEL[ownership]}</StatusPill>;
}

/** Read-only rendered Markdown. ReactMarkdown escapes embedded HTML by default. */
export function GovernanceMarkdown({ markdown }: { readonly markdown: string }): React.JSX.Element {
  if (markdown.length === 0) {
    return <p className="text-sm text-text-subtle">No Markdown has been recorded yet.</p>;
  }
  return (
    <div className="space-y-3 text-sm leading-relaxed text-text" data-markdown-preview>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
    </div>
  );
}

export interface GovernanceEditorProps {
  readonly component: GovernanceComponentView;
  readonly markdown: string;
  readonly saving: boolean;
  readonly saveNote: string | null;
  readonly onChange: (markdown: string) => void;
  readonly onSave: () => void;
}

function GovernanceEditorHeader({
  component,
  onFile,
}: {
  readonly component: GovernanceComponentView;
  readonly onFile: (event: React.ChangeEvent<HTMLInputElement>) => void;
}): React.JSX.Element {
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
      <div>
        <h3 className="text-sm font-semibold text-text">{component.label}</h3>
        <p className="mt-1 text-xs text-text-subtle">
          Saving creates a new active version and preserves the complete history.
        </p>
      </div>
      <label className="btn ghost sm cursor-pointer">
        Upload .md
        <input
          type="file"
          accept=".md,text/markdown"
          aria-label="Upload Markdown file"
          className="sr-only"
          onChange={onFile}
        />
      </label>
    </div>
  );
}

function GovernanceEditorFields({
  component,
  markdown,
  onChange,
}: Pick<GovernanceEditorProps, 'component' | 'markdown' | 'onChange'>): React.JSX.Element {
  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <label className="block">
        <span className="mb-2 block text-xs font-medium uppercase tracking-wide text-text-subtle">
          Markdown
        </span>
        <textarea
          aria-label={`${component.label} Markdown`}
          value={markdown}
          onChange={(event) => onChange(event.currentTarget.value)}
          spellCheck={false}
          className="min-h-96 w-full resize-y rounded-lg border border-border bg-surface px-4 py-3 font-mono text-sm leading-relaxed text-text"
        />
      </label>
      <div>
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-text-subtle">
          Preview
        </div>
        <Panel className="min-h-96 overflow-auto px-5 py-4">
          <GovernanceMarkdown markdown={markdown} />
        </Panel>
      </div>
    </div>
  );
}

function GovernanceEditorFooter({
  changed,
  saving,
  saveNote,
  uploadNote,
  onSave,
}: Pick<GovernanceEditorProps, 'saving' | 'saveNote' | 'onSave'> & {
  readonly changed: boolean;
  readonly uploadNote: string | null;
}): React.JSX.Element {
  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
      <div role="status" className="text-sm text-text-subtle">
        {saveNote ?? uploadNote ?? (changed ? 'Unsaved changes.' : 'Current version loaded.')}
      </div>
      <Btn onClick={onSave} disabled={!changed || saving}>
        {saving ? 'Saving…' : 'Save new version'}
      </Btn>
    </div>
  );
}

/** Company-component editor with a local `.md` import buffer and adjacent rendered preview. */
export function GovernanceEditor({
  component,
  markdown,
  saving,
  saveNote,
  onChange,
  onSave,
}: GovernanceEditorProps): React.JSX.Element {
  const [uploadNote, setUploadNote] = useState<string | null>(null);
  const changed = markdown !== component.markdown;

  const onFile = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (file === undefined) return;
    if (!file.name.toLowerCase().endsWith('.md')) {
      setUploadNote('Choose a Markdown file ending in .md.');
      return;
    }
    try {
      onChange(await file.text());
      setUploadNote(`${file.name} loaded. Save to create the next version.`);
    } catch {
      setUploadNote('That Markdown file could not be read.');
    }
  };

  return (
    <section
      aria-label={`${component.label} editor`}
      data-governance-editor={component.componentKey}
    >
      <GovernanceEditorHeader component={component} onFile={(event) => void onFile(event)} />
      <GovernanceEditorFields component={component} markdown={markdown} onChange={onChange} />
      <GovernanceEditorFooter
        changed={changed}
        saving={saving}
        saveNote={saveNote}
        uploadNote={uploadNote}
        onSave={onSave}
      />
    </section>
  );
}

/** One immutable/read-only current component card. */
export function ReadonlyGovernanceComponent({
  component,
}: {
  readonly component: GovernanceComponentView;
}): React.JSX.Element {
  return (
    <Panel className="px-5 py-4" dataMarker={{ name: 'component', value: component.componentKey }}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-text">{component.label}</h3>
          <p className="mt-1 text-xs text-text-subtle">
            {component.version === null ? 'No version yet' : `Version ${String(component.version)}`}
          </p>
        </div>
        <OwnershipLabel ownership={component.ownership} />
      </div>
      <GovernanceMarkdown markdown={component.markdown} />
    </Panel>
  );
}
